import { getEventFactTypeInfo } from "./EventTypeInfo";

// Keep saved conditions for later, but hide editing and bypass delivery filtering for now.
export const ENABLE_EVENT_CONNECTOR_CONDITIONS = false;

export const EVENT_CONDITION_OPERATORS = {
  equals: "Equals", notEquals: "Does not equal", contains: "Contains", notContains: "Does not contain",
  startsWith: "Starts with", endsWith: "Ends with", regex: "Matches regex",
  exists: "Exists", notExists: "Does not exist",
  greaterThan: "Greater than", greaterThanOrEqual: "Greater than or equal",
  lessThan: "Less than", lessThanOrEqual: "Less than or equal",
} as const;
export type EventConditionOperator = keyof typeof EVENT_CONDITION_OPERATORS;
export interface EventFactCondition {
  fact: string;
  operator: EventConditionOperator;
  value?: string;
  ignoreCase?: boolean;
}
export interface EventConditionGroup {
  logic: "and" | "or";
  conditions: EventCondition[];
}
export type EventCondition = EventFactCondition | EventConditionGroup;

/** Shared save-time validation. Empty/invalid groups must never silently disable filtering. */
export const validateEventConditions = (input: unknown): string | undefined => {
  if (input === undefined || input === null) return;
  let count = 0;
  const visit = (node: any, depth: number): string | undefined => {
    if (++count > 50 || depth > 5) return "Use at most 50 conditions and 5 group levels.";
    if (!node || typeof node !== "object" || Array.isArray(node)) return "Invalid condition.";
    if ("logic" in node || "conditions" in node) {
      if (!["and", "or"].includes(node.logic) || !Array.isArray(node.conditions) || !node.conditions.length)
        return "Each AND/OR group needs at least one condition.";
      for (const child of node.conditions) { const error = visit(child, depth + 1); if (error) return error; }
      return;
    }
    if (typeof node.fact !== "string" || !getEventFactTypeInfo(node.fact)) return "Select a registered fact.";
    if (!Object.prototype.hasOwnProperty.call(EVENT_CONDITION_OPERATORS, node.operator)) return "Select a supported operator.";
    if (node.ignoreCase !== undefined && typeof node.ignoreCase !== "boolean") return "Invalid case sensitivity.";
    if (["exists", "notExists"].includes(node.operator)) return;
    if (typeof node.value !== "string" || node.value.length > 1000) return "Condition values must be text of at most 1,000 characters.";
    if (["greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"].includes(node.operator)
      && (!node.value.trim() || !Number.isFinite(Number(node.value)))) return "Enter a valid number.";
    if (node.operator === "regex") {
      if (!node.value || node.value.length > 256) return "Regex patterns must contain 1–256 characters.";
      try { new RegExp(node.value, node.ignoreCase ? "i" : ""); } catch { return "Invalid regular expression."; }
    }
  };
  return visit(input, 0);
};
